// src/pages/workflow/Services.tsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  ExpandMore,
  Kitchen,
  Restaurant,
  CleaningServices,
  AccessTime,
  Group,
  VerifiedUser,
  LocalOffer,
  People,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Services() {
  const theme = useTheme();
  const { t } = useTranslation();
  const fServiceT = (key: string) => t(`workflowservices:${key}`);
  const services = [

    {
      id: "kitchen-helper",
      icon: <Restaurant />,
      title: fServiceT("kitchenHelper"),
      description: fServiceT("kitchenHelperDescription"),
      color: "#4caf50",
      features: [
        fServiceT("kitchenfeatures.0"),
        fServiceT("kitchenfeatures.1"),
        fServiceT("kitchenfeatures.2"),
        fServiceT("kitchenfeatures.3"),
        fServiceT("kitchenfeatures.4"),
      ],
      pricing: fServiceT("kitchenHelperPricing"),
      idealFor: ["Canteen", "Peak hour support", "Staff shortages", "Special events", "Seasonal demand"],
    },
    // {
    //   id: "waiter-support",
    //   icon: <People />, // or <SupportAgent /> or <Groups /> from @mui/icons-material
    //   title: fServiceT("waiterSupport"),
    //   description: fServiceT("waiterSupportDescription"),
    //   color: "#C33764", // Professional blue - you can change to your preferred color
    //   features: [
    //     fServiceT("waiterfeatures.0"),
    //     fServiceT("waiterfeatures.1"),
    //     fServiceT("waiterfeatures.2"),
    //     fServiceT("waiterfeatures.3"),
    //     fServiceT("waiterfeatures.4")
    //   ],
    //   pricing: fServiceT("waiterSupportPricing"),
    //   idealFor: ["Peak dining hours", "Special events and parties", "Temporary staff shortages", "Staff training periods"],
    // },
    {
      id: "dishwasher",
      icon: <Kitchen />,
      title: fServiceT("dishwashingServices"),
      description: fServiceT("dishwashingServicesDescription"),
      color: "#2196f3",
      features: [
        fServiceT("dishwashingfeatures.0"),
        fServiceT("dishwashingfeatures.1"),
        fServiceT("dishwashingfeatures.2"),
        fServiceT("dishwashingfeatures.3"),
        fServiceT("dishwashingfeatures.4"),
      ],
      pricing: fServiceT("dishwashingServicesPricing"),
      idealFor: ["Restaurants", "Cafes", "Catering companies", "Event venues"],
    },
    {
      id: "cleaner",
      icon: <CleaningServices />,
      title: fServiceT("commercialCleaning"),
      description: fServiceT("commercialCleaningDescription"),
      color: "#ff9800",
      features: [
        fServiceT("cleaningFeatures.0"),
        fServiceT("cleaningFeatures.1"),
        fServiceT("cleaningFeatures.2"),
        fServiceT("cleaningFeatures.3"),
        fServiceT("cleaningFeatures.4"),
      ],
      pricing: fServiceT("commercialCleaningPricing"),
      idealFor: ["Restaurant deep cleans", "Kitchen maintenance", "Post-event cleanup", "Regular maintenance"],
    },
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{ fontWeight: 800, fontSize: { xs: "2.5rem", md: "3.5rem" } }}
          >
           {fServiceT("processTitle")}
          </Typography>
          <Typography variant="h5" sx={{ mt: 2, opacity: 0.9 }}>
            {fServiceT("processDescription")}
          </Typography>
        </Container>
      </Box>

      {/* Services Grid */}
      <Container sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} key={index}>
              <Card
                sx={{
                  borderLeft: `6px solid ${service.color}`,
                  overflow: "visible",
                }}
              >
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        backgroundColor: `${service.color}15`,
                        height: "100%",
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={{ color: service.color, fontSize: 60, mb: 2 }}>
                        {service.icon}
                      </Box>
                      <Chip
                        label={service.pricing}
                        sx={{
                          backgroundColor: service.color,
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                        {service.title}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        {service.description}
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Services Include:
                          </Typography>
                          <List dense>
                            {service.features.map((feature, idx) => (
                              <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <CheckCircle sx={{ color: service.color }} />
                                </ListItemIcon>
                                <ListItemText primary={feature} primaryTypographyProps={{ variant: "h6" }} />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Ideal For:
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {service.idealFor.map((item, idx) => (
                              <Chip
                                key={idx}
                                label={item}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                          <Box sx={{ mt: 4 }}>
                            <Button
                              variant="contained"
                              component={Link}
                              to={`/workflow/booking?service=${service.id}`}
                              sx={{
                                backgroundColor: service.color,
                                "&:hover": {
                                  backgroundColor: service.color,
                                  opacity: 0.9,
                                },
                              }}
                            >
                              {fServiceT("ctaButton")}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: "#f8f9fa", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 800 }}>
            {fServiceT("frequentQuestionsTitle")}
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 6 }}>
            {fServiceT("needtoKnowInfo")}
          </Typography>
          {[
            {
              q: fServiceT("howQuickly"),
              a: fServiceT("quicklyAnswer"),
            },
            {
              q: fServiceT("equipment"),
              a: fServiceT("equipmentAnswer"),
            },
            {
              q: fServiceT("trainedStaff"),
              a: fServiceT("trainedStaffAnswer"),
            },
          ].map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA */}
      <Container sx={{ py: { xs: 8, md: 12 }, textAlign: "center" }}>
        <Box
          sx={{
            border: "2px dashed",
            borderColor: "primary.main",
            borderRadius: 3,
            p: { xs: 4, md: 8 },
          }}
        >
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 800 }}>
            {fServiceT("customSolution")}
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 5 }}>
            {fServiceT("customSolutionDescription")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/contact"
            sx={{ py: 1.5, px: 6, fontSize: "1.1rem" }}
          >
            {fServiceT("customQuote")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}